import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectGroup,
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";

const Quote = () => {
  const { toast } = useToast();
  const [vehicle, setVehicle] = useState({
    year: "",
    make: "",
    model: "",
    type: "regular" // default to regular
  });

  const [services, setServices] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [quoteDetails, setQuoteDetails] = useState<{[key: string]: number}>({});
  const [quoteSaved, setQuoteSaved] = useState(false); // New state to track if quote is saved

  // Services from Services.tsx
  const serviceOptions = [
    {
      id: "eco-wash",
      title: "ECO Wash Package",
      description: "Waterless Exterior Hand Wash, Vacuum Interior, Dashboard Wipe, Clean Windows",
      price: {
        regular: 40,
        suv: 50
      }
    },
    {
      id: "complete-detail",
      title: "Complete Detail Package",
      description: "Complete Interior & Exterior Detail with Polish",
      price: {
        regular: 300,
        suv: 350
      }
    },
    {
      id: "interior-detail",
      title: "Interior Detail Package",
      description: "Full Interior Cleaning with Steam and Odor Elimination",
      price: {
        regular: 170,
        suv: 190
      }
    },
    {
      id: "eco-wax",
      title: "ECO Wax Package",
      description: "ECO Wash with Hand-Applied Premium Wax",
      price: {
        regular: 80,
        suv: 100
      }
    },
    {
      id: "exterior-detailing",
      title: "Exterior Detailing Package",
      description: "Hand Polish with Premium Compounds and Paint Decontamination",
      price: {
        regular: 190,
        suv: 210
      }
    },
    {
      id: "ceramic-coatings",
      title: "Ceramic Coatings",
      description: "Professional-Grade Ceramic Coating with 3-5 Years Protection",
      price: {
        regular: 1000,
        suv: 1200
      }
    },
    {
      id: "wheel-restoration",
      title: "Wheel Restoration",
      description: "Wheel Repair and Professional Refinishing",
      price: {
        perWheel: 125
      }
    },
    {
      id: "headlight-restoration",
      title: "Headlight Restoration",
      description: "Multi-Stage Wet Sanding and UV Sealant Application",
      price: {
        flat: 100
      }
    }
  ];

  // Calculate the quote whenever services or vehicle type changes
  useEffect(() => {
    let newTotal = 0;
    const newQuoteDetails: {[key: string]: number} = {};

    services.forEach(serviceId => {
      const service = serviceOptions.find(s => s.id === serviceId);
      if (service) {
        if ('price' in service) {
          if ('regular' in service.price && 'suv' in service.price) {
            const price = vehicle.type === 'suv' ? service.price.suv : service.price.regular;
            newTotal += price;
            newQuoteDetails[service.title] = price;
          } else if ('perWheel' in service.price) {
            // Assume all 4 wheels for the quote
            const price = service.price.perWheel * 4;
            newTotal += price;
            newQuoteDetails[service.title] = price;
          } else if ('flat' in service.price) {
            newTotal += service.price.flat;
            newQuoteDetails[service.title] = service.price.flat;
          }
        }
      }
    });

    // Add mobile service fee if needed
    if (newTotal > 0 && newTotal < 100) {
      newQuoteDetails["Mobile Service Fee"] = 100 - newTotal;
      newTotal = 100; // Minimum charge for mobile service is $100
    }

    setTotal(newTotal);
    setQuoteDetails(newQuoteDetails);
  }, [services, vehicle.type, serviceOptions]);

  const handleServiceToggle = (serviceId: string) => {
    setServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleSaveQuote = () => {
    // You could save this to localStorage, send it to an API, etc.
    // For now, we'll just show a toast
    toast({
      title: "Quote Saved",
      description: `Your quote for $${total} has been saved. You can book this service now.`,
    });
    setQuoteSaved(true); // Set quoteSaved to true
  };

  const carBrands = [
    "Acura", "Alfa Romeo", "Aston Martin", "Audi", "Bentley", "BMW", "Buick",
    "Cadillac", "Chevrolet", "Chrysler", "Dodge", "Ferrari", "Fiat", "Ford",
    "Genesis", "GMC", "Honda", "Hyundai", "Infiniti", "Jaguar", "Jeep", "Kia",
    "Lamborghini", "Land Rover", "Lexus", "Lincoln", "Maserati", "Mazda",
    "McLaren", "Mercedes-Benz", "Mini", "Mitsubishi", "Nissan", "Porsche",
    "Ram", "Rolls-Royce", "Subaru", "Tesla", "Toyota", "Volkswagen", "Volvo"
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Auto Detailing Quote Calculator",
    "description": "Get a free online quote for professional auto detailing services in the Twin Cities area",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Mobile Detail Co.",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "MN",
        "addressLocality": "Twin Cities"
      }
    }
  };

  return (
    <>
      <SEO
        title="Free Auto Detailing Quote | Mobile Detail Co."
        description="Get a free online quote for professional auto detailing services in the Twin Cities area. Calculate your price based on your vehicle and desired services."
        url="https://mobiledetailco.com/quote"
        jsonLd={structuredData}
      />
      <div className="min-h-screen pt-24 bg-gradient-to-b from-white to-primary-light">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primary text-center mb-8">
            Get Your Free Quote
          </h1>
          <p className="text-xl text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Select your vehicle details and desired services to get an instant price quote. Mobile services have a minimum charge of $100.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Vehicle Info Card */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Vehicle Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="vehicleType">Vehicle Type</Label>
                  <Select 
                    value={vehicle.type} 
                    onValueChange={(value) => setVehicle({...vehicle, type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular/Sedan</SelectItem>
                      <SelectItem value="suv">SUV/Truck</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="make">Make</Label>
                  <Select 
                    value={vehicle.make} 
                    onValueChange={(value) => setVehicle({...vehicle, make: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {carBrands.map(brand => (
                          <SelectItem key={brand} value={brand.toLowerCase()}>{brand}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input 
                    id="model" 
                    placeholder="e.g. Camry, F-150" 
                    value={vehicle.model}
                    onChange={(e) => setVehicle({...vehicle, model: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="year">Year</Label>
                  <Input 
                    id="year" 
                    placeholder="e.g. 2022" 
                    value={vehicle.year}
                    onChange={(e) => setVehicle({...vehicle, year: e.target.value})}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Services Card */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Select Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {serviceOptions.map((service) => (
                    <div key={service.id} className="flex items-start space-x-2">
                      <Checkbox
                        id={service.id}
                        checked={services.includes(service.id)}
                        onCheckedChange={() => handleServiceToggle(service.id)}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor={service.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {service.title}
                          {service.id === "wheel-restoration" && (
                            <span className="text-xs text-gray-500 ml-1">(per wheel)</span>
                          )}
                        </label>
                        <p className="text-sm text-gray-500">{service.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quote Summary Card */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Your Quote</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(quoteDetails).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(quoteDetails).map(([service, price]) => (
                        <div key={service} className="flex justify-between">
                          <span>{service}</span>
                          <span>${price}</span>
                        </div>
                      ))}
                      <div className="border-t border-gray-200 pt-2 mt-4">
                        <div className="flex justify-between font-bold">
                          <span>Total Estimate</span>
                          <span>${total}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">Select services to see your quote</p>
                  )}

                  <div className="pt-4 space-y-4">
                    <p className="text-sm text-gray-500">
                      * This is an approximate quote. Final price may vary based on vehicle condition and specific requirements.
                    </p>
                    <p className="text-sm text-gray-500">
                      * Mobile services have a minimum charge of $100.
                    </p>
                    <p className="text-sm text-gray-500">
                      * For wheel restoration, price is per wheel.
                    </p>
                  </div>

                  <div className="space-y-2 pt-4">
                    <Button 
                      className="w-full" 
                      onClick={handleSaveQuote}
                      disabled={services.length === 0}
                    >
                      Save Quote
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      asChild
                      disabled={services.length === 0}
                    >
                      <Link to="/booking">Book Now</Link>
                    </Button>
                  </div>

                  {quoteSaved && (
                    <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                      <h3 className="text-xl font-semibold text-primary mb-3">Ready to schedule your appointment?</h3>
                      <p className="text-gray-700 mb-2">
                        Call us at: <a href="tel:+16512869930" className="text-primary hover:underline font-medium">(651) 286-9930</a>
                      </p>
                      <p className="text-gray-700">
                        Or email us at: <a href="mailto:support@mobiledetailco.com" className="text-primary hover:underline font-medium">support@mobiledetailco.com</a>
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Quote; 